/**
 * Backend Configuration Utilities
 *
 * Client-side utilities for managing backend URL selection, localStorage persistence,
 * health check validation, and configuration fetching.
 */

import type { BackendConfig, BackendOption } from './types/backend-config';
import { logger } from './logger';

// Header name for passing backend URL to API routes
export const BACKEND_URL_HEADER = 'X-Backend-URL';

// localStorage key for persisting selected backend URL
const STORAGE_KEY = 'v-agent-backend-url';

// Re-export shared types for convenience
export type { BackendOption, BackendConfig };

// Client-side cache for fetched configuration
let configCache: BackendConfig | null = null;

/**
 * Get the currently selected backend URL from localStorage
 * @returns The selected backend URL, or null if not set
 */
export function getSelectedBackendUrl(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    logger.error('[backend-config] localStorage access failed:', error);
    return null;
  }
}

/**
 * Save the selected backend URL to localStorage
 * @param url The backend URL to save
 */
export function setSelectedBackendUrl(url: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, url);
  } catch (error) {
    logger.error('[backend-config] localStorage write failed:', error);
  }
}

/**
 * Clear the selected backend URL from localStorage
 */
export function clearSelectedBackendUrl(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.error('[backend-config] localStorage clear failed:', error);
  }
}

/**
 * Fetch backend configuration from the API endpoint
 * Uses client-side caching to minimize API calls
 *
 * @param forceRefresh If true, bypass cache and fetch fresh config
 * @returns Complete backend configuration including backends and cloudfront
 */
export async function fetchBackendConfig(forceRefresh = false): Promise<BackendConfig> {
  // Return cached config if available and not forcing refresh
  if (configCache && !forceRefresh) {
    return configCache;
  }

  try {
    const response = await fetch('/api/backend-config', {
      cache: 'no-store', // Disable browser caching
    });

    if (!response.ok) {
      throw new Error(`Config fetch failed with status: ${response.status}`);
    }

    const config: BackendConfig = await response.json();

    // Cache the result
    configCache = config;

    return config;
  } catch (error) {
    logger.error('[backend-config] Failed to fetch backend config:', error);

    // Return fallback if cache exists, otherwise throw
    if (configCache) {
      return configCache;
    }

    throw error;
  }
}

// NOTE: Backend health validation functionality has been deprecated in favor of
// the dedicated /api/health endpoint for app health checks. This code is kept
// commented out for reference but is no longer used.
//
// /**
//  * Validate backend connectivity by calling the health endpoint
//  * Uses the /api/health proxy route to avoid mixed content errors
//  *
//  * @param backendUrl The backend URL to validate
//  * @returns true if backend is reachable and healthy, false otherwise
//  */
// export async function validateBackendHealth(backendUrl: string): Promise<boolean> {
//   try {
//     // Create AbortController for 5-second timeout
//     const controller = new AbortController();
//     const timeoutId = setTimeout(() => controller.abort(), 5000);
//
//     // Use the proxy route to avoid mixed content errors
//     const response = await fetch('/api/health', {
//       method: 'GET',
//       headers: {
//         [BACKEND_URL_HEADER]: backendUrl,
//       },
//       signal: controller.signal,
//       cache: 'no-store',
//     });
//
//     clearTimeout(timeoutId);
//
//     // Consider HTTP 200 as healthy
//     return response.ok;
//   } catch (error) {
//     logger.error('[backend-config] Health check failed for', backendUrl, error);
//     return false;
//   }
// }

/**
 * Get the default backend URL from the first config option
 * Used when no backend URL is saved in localStorage
 *
 * @returns The default backend URL
 */
export async function getDefaultBackendUrl(): Promise<string> {
  try {
    const config = await fetchBackendConfig();
    return config.backends[0]?.url || 'http://localhost:8000';
  } catch (error) {
    logger.error('[backend-config] Failed to get default backend URL:', error);
    return 'http://localhost:8000';
  }
}

/**
 * Get the CloudFront queries URL from configuration
 * Returns empty string if not configured (will fallback to hardcoded questions)
 *
 * @returns The CloudFront base URL for queries, or empty string
 */
export async function getCloudFrontQueriesUrl(): Promise<string> {
  try {
    const config = await fetchBackendConfig();
    return config.cloudfront?.queriesUrl || '';
  } catch (error) {
    logger.error('[backend-config] Failed to get CloudFront URL:', error);
    return '';
  }
}
